import { useState, useCallback } from 'react';
import { pw9205Api, F9051 } from '../../../services/pw9205Api';

export function usePW9205Lookup() {
  const [names, setNames] = useState<Record<string, string>>({});

  const resolveSingleName = useCallback(async (nameKey: string, type: string, code: string) => {
    const name = await pw9205Api.lookupName(type, code);
    setNames(prev => ({ ...prev, [nameKey]: name }));
  }, []);

  /** Load current proxies from F905 and populate names */
  const loadEmployeeProxies = useCallback(async (empCode: string): Promise<Partial<F9051>> => {
    if (!empCode || !empCode.trim()) return {};
    const proxy = await pw9205Api.getProxy(empCode);
    // Resolve names for the original proxies
    const proxyFields: Array<[string, string]> = [
      ['origProxy1', (proxy.f9051_11 as string) || ''],
      ['origProxy2', (proxy.f9051_12 as string) || ''],
      ['origProxy3', (proxy.f9051_13 as string) || ''],
      ['origProxy4', (proxy.f9051_14 as string) || ''],
      ['origProxy5', (proxy.f9051_15 as string) || ''],
    ];
    const resolvedNames: Record<string, string> = {};
    await Promise.all(
      proxyFields
        .filter(([, code]) => code)
        .map(([key, code]) =>
          pw9205Api.lookupName('employee', code).then(name => {
            resolvedNames[key] = name;
          })
        )
    );
    setNames(prev => ({ ...prev, ...resolvedNames }));
    return proxy;
  }, []);

  const resolveAllNames = useCallback(async (master: Partial<F9051>) => {
    const entries: Array<[string, string, string]> = [];

    if (master.f9051_02) entries.push(['employee', 'employee', master.f9051_02]);
    if (master.f9051_11) entries.push(['origProxy1', 'employee', master.f9051_11]);
    if (master.f9051_12) entries.push(['origProxy2', 'employee', master.f9051_12]);
    if (master.f9051_13) entries.push(['origProxy3', 'employee', master.f9051_13]);
    if (master.f9051_14) entries.push(['origProxy4', 'employee', master.f9051_14]);
    if (master.f9051_15) entries.push(['origProxy5', 'employee', master.f9051_15]);
    if (master.f9051_31) entries.push(['newProxy1', 'employee', master.f9051_31]);
    if (master.f9051_32) entries.push(['newProxy2', 'employee', master.f9051_32]);
    if (master.f9051_33) entries.push(['newProxy3', 'employee', master.f9051_33]);
    if (master.f9051_34) entries.push(['newProxy4', 'employee', master.f9051_34]);
    if (master.f9051_35) entries.push(['newProxy5', 'employee', master.f9051_35]);

    if (entries.length === 0) return;

    const results = await Promise.all(
      entries.map(([key, type, code]) =>
        pw9205Api.lookupName(type, code).then(name => [key, name] as [string, string])
      )
    );
    setNames(prev => {
      const next = { ...prev };
      for (const [key, name] of results) next[key] = name;
      return next;
    });
  }, []);

  const clearNames = useCallback(() => {
    setNames({});
  }, []);

  return { names, resolveSingleName, resolveAllNames, clearNames, loadEmployeeProxies };
}
